import { useQuery } from "@tanstack/react-query";
import { startTransition, useEffect } from "react";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeItemContext,
  createStepNode,
} from "~/components/build/Show/routes/list/-components/TreeNode";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { CollapsibleStepItem } from "~/components/build/Show/routes/list/-components/TreeNode/CollapsibleStepItem";
import {
  FilterByOption,
  filterSteps,
  sortSteps,
  stepDirectlyMatchesFilters,
} from "~/components/build/Show/lib/useFilterStore";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { GroupStep, Step } from "~/lib/pipeline";
import { childrenStepsQueryOptions } from "~/components/build/Show/lib/queries/steps";

const GroupStepItem = ({ step, ...props }: { step: GroupStep } & TreeItemContext) => {
  const { build } = useBuild();
  const setSteps = useBuildPageStore((state) => state.setSteps);
  const stepsGroupedByParent = useBuildPageStore((state) => state.stepsGroupedByParent);

  const query = useQuery(
    childrenStepsQueryOptions(build, step.uuid, {
      enabled:
        props.collapsibleState === CollapsibleState.Expanded ||
        props.collapsibleState === CollapsibleState.Full,
    }),
  );

  useEffect(() => {
    if (query.data) {
      startTransition(() => {
        setSteps(query.data, build);
      });
    }
  }, [query.data]);

  // Show loading state when initial fetch is in progress and we haven't populated the store with all the children.
  const showLoadingState =
    query.isLoading &&
    stepsGroupedByParent.get(step.uuid)?.length !== step.statistics?.stepChildrenCount;

  return <CollapsibleStepItem {...props} step={step} isLoading={showLoadingState} />;
};

export const createGroupStepNode = (step: GroupStep, build: Build): TreeNode => ({
  id: step.uuid,
  height: STEP_ITEM_HEIGHT,
  sticky: true,
  render: (props) => <GroupStepItem step={step} {...props} />,
  childNodes: (collapsibleState: CollapsibleState, filters: FilterByOption[]) => {
    if (collapsibleState === CollapsibleState.Expanded) {
      const steps: Step[] = useBuildPageStore.getState().stepsGroupedByParent.get(step.uuid) || [];
      const { showSkippedSteps } = useBuildPreferencesStore.getState();
      let filteredSteps = filterSteps(steps, filters, build, { showSkippedSteps });
      filteredSteps = sortSteps(filteredSteps);

      return filteredSteps.map((step) => createStepNode(step, build));
    }
    return [];
  },
});

/**
 * Create a group step node scoped to a specific state section.
 *
 * Used in the state-grouped view (DisplayMode.State) where a group may appear
 * under multiple state headers. Children are filtered to only those matching
 * the given state. The node ID is namespaced to avoid collisions when the same
 * group appears in multiple state sections.
 *
 * Groups in state view are always expanded and cannot be collapsed, since the
 * state header already provides the grouping context.
 */
export const createGroupStepNodeForState = (
  step: GroupStep,
  build: Build,
  stateFilter: FilterByOption,
): TreeNode => {
  const groupMatchesState = stepDirectlyMatchesFilters(step, [stateFilter], build);

  return {
    id: `${step.uuid}_${stateFilter}`,
    height: STEP_ITEM_HEIGHT,
    sticky: true,
    deemphasized: !groupMatchesState,
    forcedCollapsibleState: CollapsibleState.Expanded,
    render: (props) => <CollapsibleStepItem step={step} {...props} />,
    childNodes: () => {
      const steps: Step[] = useBuildPageStore.getState().stepsGroupedByParent.get(step.uuid) || [];
      let filteredSteps = filterSteps(steps, [stateFilter], build);
      filteredSteps = sortSteps(filteredSteps);

      return filteredSteps.map((childStep) => createStepNode(childStep, build));
    },
  };
};
